import {
  Award,
  Bell,
  BellRing,
  BookMarked,
  BookOpen,
  Building2,
  Calendar,
  CalendarDays,
  Bus,
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Contact,
  FileText,
  Flag,
  Home,
  IndianRupee,
  KeyRound,
  Layers,
  LifeBuoy,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  Package,
  School,

  ShieldCheck,
  Sparkles,
  Users,
  User,
  Video,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import type { InstitutionType } from '@/constants/institute';
import { featureName, type FeatureKey } from '@/constants/features';
import type { Permission } from '@/constants/permissions';

/**
 * Every menu in the product, in one place.
 *
 * Four panels, four lists — and nothing here decides how any of them *look*.
 * The staff consoles draw theirs through `ConsoleLayout`'s dark sidebar and the
 * student panel draws its own in its own colours; both read their options from
 * this file. Declaring what is on a menu and deciding how a menu is painted are
 * two jobs, and keeping them apart is what lets the designs stay different
 * while the answer to "what sections does this product have?" stays in one
 * place.
 *
 * Adding a section is one entry here plus a route. Renaming one is one edit,
 * not four files hunted down.
 */

/** One entry on any panel's menu. */
export interface NavOption {
  /**
   * Stable id for the section, used where a screen has to say which one it is
   * drawing rather than which address it is at.
   */
  key: string;
  /** Its address. The panel reads which section to draw from the path. */
  to: string;
  label: string;
  icon: LucideIcon;
  /** Exact match only — for a root path that would otherwise match everything. */
  end?: boolean;
  /**
   * The heading this entry sits under, for a menu long enough to need them.
   *
   * `ConsoleLayout` draws the heading once, above the first entry carrying it,
   * so a group is declared by the items in it rather than by a second list of
   * sections that has to be kept in step with this one. Consecutive entries
   * sharing a group are one block; a menu that leaves it out draws as one list,
   * which is what the shorter menus above still do.
   */
  group?: string;
  /**
   * Hidden unless the signed-in account holds this key. `ConsoleLayout` applies
   * it, so no console has to remember to. The server decides either way — this
   * only stops a link being offered to a screen that would be refused.
   */
  needs?: Permission;
  /**
   * Hidden — and its screens replaced by a "not enabled" notice — unless the
   * school has this feature active on the Feature tracker. Every school-facing
   * entry names one; only the platform console's menu leaves it off.
   */
  feature?: FeatureKey;
  /**
   * Keeps this entry's own label instead of taking the feature's catalogue
   * name — for wording one panel needs, such as a college's "Assignments".
   */
  keepLabel?: boolean;
  /**
   * Additional subpaths or route prefixes that should activate this navigation option.
   */
  matchPaths?: string[];
}

// --- the institute administering itself -------------------------------------

export const ADMIN_NAV: readonly NavOption[] = [
  { key: 'dashboard', feature: 'DASHBOARD', to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  {
    key: 'institute', feature: 'INSTITUTE_PROFILE',
    to: '/admin/institute',
    label: 'Institute Profile',
    icon: School,
    needs: 'INSTITUTE_PROFILE_VIEW',
  },
  { key: 'classes', feature: 'CLASSES', to: '/admin/classes', label: 'Classes', icon: Layers, needs: 'CLASS_VIEW' },
  // The list each class picks its core subjects, electives and languages from.
  // The class keys open it: a subject list is part of setting classes up.
  { key: 'subjects', feature: 'SUBJECTS', to: '/admin/subjects', label: 'Subjects', icon: BookMarked, needs: 'CLASS_VIEW' },
  // Classes, then the staff who teach them, then the timetable built from both:
  // the order an institute sets itself up in.
  { key: 'staff', feature: 'STAFF', to: '/admin/staff', label: 'Staff', icon: Contact, needs: 'STAFF_VIEW' },
  {
    key: 'timetable', feature: 'TIMETABLE',
    to: '/admin/timetable',
    label: 'Timetable',
    icon: CalendarClock,
    needs: 'TIMETABLE_VIEW',
  },
  { key: 'students', feature: 'STUDENT_RECORDS', to: '/admin/students', label: 'Students', icon: Users, needs: 'STUDENT_VIEW' },
  // School-wide houses, beside the register whose students they hold.
  { key: 'houses', feature: 'HOUSES', to: '/admin/houses', label: 'Houses', icon: Flag, needs: 'STUDENT_VIEW' },
  // Next to the roll on purpose: the register and the fee counter read the same
  // list of children, and an office moves between them all morning.
  { key: 'fees', feature: 'FEES', to: '/admin/fees', label: 'Fees', icon: IndianRupee, needs: 'FEE_VIEW' },
  { key: 'payroll', feature: 'PAYROLL', to: '/admin/payroll', label: 'Payroll', icon: Wallet, needs: 'PAYROLL_VIEW' },
  // No `needs` yet: the transport module has no permission of its own until the
  // endpoints exist. Whether the school has it at all is the TRANSPORT feature.
  { key: 'transport', feature: 'TRANSPORT', to: '/admin/transport', label: 'Transport', icon: Bus },
  {
    key: 'staff-attendance', feature: 'STAFF_ATTENDANCE',
    to: '/admin/staff-attendance',
    label: 'Staff Attendance',
    icon: ClipboardCheck,
    needs: 'STAFF_ATTENDANCE_VIEW',
  },
  { key: 'calendar', feature: 'CALENDAR', to: '/admin/calendar', label: 'Academic Calendar', icon: Calendar, needs: 'CALENDAR_VIEW' },
  {
    key: 'notices', feature: 'INSTITUTE_NOTICE',
    to: '/admin/notices',
    label: 'Notice Board',
    icon: Megaphone,
    needs: 'INSTITUTE_NOTICE_MANAGE',
  },
  // The office's SMS and WhatsApp sends, composed and reviewed. The parent's
  // Notifications page reads the same sends, so one feature covers both; every
  // request behind this one needs MESSAGE_SEND, so a role without the key is
  // not offered a page that would only be refused.
  {
    key: 'notifications', feature: 'MESSAGING',
    to: '/admin/notifications',
    // The admin's side of the feature is the sending; the parent's page keeps
    // the catalogue's "Notifications".
    label: 'Send notifications',
    keepLabel: true,
    icon: Bell,
    needs: 'MESSAGE_SEND',
  },
  {
    key: 'support', feature: 'SUPPORT',
    to: '/admin/support',
    label: 'Support',
    icon: LifeBuoy,
    needs: 'SUPPORT_TICKET_MANAGE',
  },
  { key: 'roles', feature: 'ROLES', to: '/admin/roles', label: 'Roles', icon: ShieldCheck, needs: 'ROLE_VIEW' },
  // The administrator's own account — also reached from their name in the top
  // bar. The same catalogue row as the student and parent My Profile.
  { key: 'profile', feature: 'MY_PROFILE', to: '/admin/profile', label: 'My Profile', icon: User },
  { key: 'password', feature: 'CHANGE_PASSWORD', to: '/admin/password', label: 'Change password', icon: KeyRound },
];

// --- our own console --------------------------------------------------------

export const PLATFORM_NAV: readonly NavOption[] = [
  { key: 'dashboard', to: '/platform', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { key: 'institutes', to: '/platform/institutes', label: 'Institutes', icon: Building2 },
  { key: 'subscriptions', to: '/platform/subscriptions', label: 'Subscription payments', icon: Wallet },
  { key: 'support', to: '/platform/support', label: 'Support tickets', icon: LifeBuoy },
  { key: 'features', to: '/platform/features', label: 'Feature tracker', icon: ListChecks },
  { key: 'catalogue', to: '/platform/catalogue', label: 'Feature catalogue', icon: Package },
];

// --- the teacher console ----------------------------------------------------

/**
 * What a teacher does, in the order a teaching day does it.
 *
 * Read by the sidebar that links to each section *and* by the dashboard that
 * titles itself after the one it is drawing — they were two lists with the same
 * labels in them, which is one label away from disagreeing.
 *
 * Worded for a school; `teacherNav` below re-words the one entry a college
 * reads differently.
 */
const TEACHER_SECTIONS: readonly NavOption[] = [
  { key: 'overview', feature: 'DASHBOARD', to: '/teacher/dashboard', label: 'Dashboard', icon: BookOpen },
  { key: 'attendance', feature: 'ATTENDANCE', to: '/teacher/attendance', label: 'Attendance', icon: CheckCircle2, needs: 'ATTENDANCE_VIEW' },
  {
    key: 'leave', feature: 'LEAVE_REQUESTS',
    to: '/teacher/leave-requests',
    label: 'Leave Requests',
    icon: CalendarCheck,
    needs: 'ATTENDANCE_VIEW',
  },
  {
    key: 'homework', feature: 'HOMEWORK',
    to: '/teacher/homework',
    label: 'Homework',
    icon: FileText,
    matchPaths: ['/teacher/homework', '/homework'],
    needs: 'HOMEWORK_VIEW',
  },
  {
    key: 'activities', feature: 'ACTIVITY',
    to: '/teacher/activities',
    label: 'Class Activities',
    icon: Sparkles,
    matchPaths: ['/teacher/activities', '/activities'],
    needs: 'ACTIVITY_VIEW',
  },
  {
    key: 'videos', feature: 'RECORDING',
    to: '/teacher/videos',
    label: 'Recorded Classes',
    icon: Video,
    matchPaths: ['/teacher/videos', '/teacher/recorded-classes'],
    needs: 'RECORDING_VIEW',
  },
  // Their own week needs no permission at the API, but the menu follows the
  // Roles screen: a role without the Timetable page does not see it.
  { key: 'timetable', feature: 'TIMETABLE', to: '/teacher/timetable', label: 'Timetable', icon: Calendar, needs: 'TIMETABLE_VIEW' },
  // The office's planner, read-only and narrowed by the server to what was
  // addressed to teachers. Same label and feature as every other panel's.
  { key: 'calendar', feature: 'CALENDAR', to: '/teacher/calendar', label: 'Academic Calendar', icon: CalendarDays, needs: 'CALENDAR_VIEW' },
  {
    key: 'marks', feature: 'EXAM',
    to: '/teacher/marks',
    label: 'Exams',
    icon: Award,
    matchPaths: ['/teacher/marks', '/teacher/exams'],
    needs: 'EXAM_VIEW',
  },
  { key: 'notices', feature: 'CLASS_NOTICE', to: '/teacher/notices', label: 'Notices', icon: Megaphone, needs: 'NOTICE_VIEW', keepLabel: true },
  // A class teacher's own class only; the page says so to anyone who is not one.
  { key: 'fee-reminders', feature: 'FEE_REMINDERS', to: '/teacher/fee-reminders', label: 'Fee Reminders', icon: BellRing, needs: 'FEE_REMIND' },
  {
    key: 'daily-report', feature: 'REPORTS',
    to: '/teacher/daily-report',
    label: 'Daily Report',
    icon: ClipboardList,
    matchPaths: ['/teacher/daily-report'],
    needs: 'DAILY_REPORT_VIEW',
  },
  {
    key: 'profile',
    to: '/teacher/profile',
    label: 'My Profile',
    icon: User,
    matchPaths: ['/teacher/profile'],
  },
  // My Profile's Settings tab, given its own entry so a teacher finds it the way
  // an administrator finds theirs — the same label, icon and feature switch.
  { key: 'password', feature: 'CHANGE_PASSWORD', to: '/teacher/settings', label: 'Change password', icon: KeyRound },
];

/**
 * The sections as the institute being taught at names them.
 *
 * A school sets homework, a college sets assignments — it is one screen, but
 * the sidebar link, the address behind it and the title the page gives itself
 * all have to agree, so all three are read from here rather than spelled out in
 * each of the three places (§3).
 *
 * Anything other than a college — including branding that has not arrived yet —
 * reads as a school, which is what the console showed before.
 */
export function teacherNav(institutionType: InstitutionType | undefined): readonly NavOption[] {
  if (institutionType !== 'COLLEGE') return TEACHER_SECTIONS;

  return TEACHER_SECTIONS.map((section) =>
    section.key === 'homework'
      ? { ...section, to: '/teacher/assignments', label: 'Assignments', keepLabel: true }
      : section,
  );
}

// --- the student panel ------------------------------------------------------

/**
 * What a child sees, in the order they look for it.
 *
 * Carries a real `to` like every other menu here. It used to be a list of tabs
 * with the addresses kept in a second map beside it, which is two lists to keep
 * in step — and a tab held in state alone is a screen nobody can link to,
 * bookmark or reload without being thrown back to the overview.
 */
export const STUDENT_NAV: readonly NavOption[] = [
  { key: 'overview', feature: 'DASHBOARD', to: '/student', label: 'Dashboard', icon: BookOpen, end: true },
  { key: 'timetable', feature: 'TIMETABLE', to: '/student/timetable', label: 'Timetable', icon: Calendar },
  { key: 'homework', feature: 'HOMEWORK', to: '/student/homework', label: 'Homework', icon: FileText },
  // Both boards live behind this one entry — their class's and the institute's.
  { key: 'notices', feature: 'CLASS_NOTICE', to: '/student/notices', label: 'Class Notices', icon: Megaphone },
  { key: 'calendar', feature: 'CALENDAR', to: '/student/calendar', label: 'Academic Calendar', icon: CalendarDays },
  { key: 'lectures', feature: 'RECORDING', to: '/student/lectures', label: 'Recorded Classes', icon: Video },
  { key: 'marks', feature: 'EXAM', to: '/student/marks', label: 'Exams', icon: Award },
  // The teacher's Leave Requests catalogue row: one switch for both ends of it.
  { key: 'leave', feature: 'LEAVE_REQUESTS', to: '/student/leave-requests', label: 'Leave Requests', icon: CalendarClock },
  // Their own ledger only. `FEE_VIEW` is the office's key to the register and
  // is deliberately not asked for — the endpoint answers for the caller.
  // { key: 'fees', feature: 'FEES', to: '/student/fees', label: 'Fees', icon: IndianRupee },
  { key: 'profile', feature: 'MY_PROFILE', to: '/student/profile', label: 'My Profile', icon: User },
  // Settings is now a tab inside My Profile beside Change Password.
  // { key: 'settings', feature: 'SETTINGS', to: '/student/settings', label: 'Settings', icon: Settings },
];

// --- the parent panel -------------------------------------------------------

/**
 * What a parent comes to look at, grouped the way they think about it.
 *
 * Four headings rather than one long list, because a parent's menu asks two
 * different questions — how is my child doing, and what is the school doing —
 * and twelve links with nothing between them makes both of them harder to find.
 * The groups are declared on the entries themselves; `ConsoleLayout` draws them.
 *
 * Every address is under `/parent`, so a parent's panel is one area of the
 * product the way `/admin` and `/teacher` are, and `isPathForRole` can keep
 * anybody else out of it.
 */
export const PARENT_NAV: readonly NavOption[] = [
  { key: 'overview', feature: 'DASHBOARD', to: '/parent', label: 'Dashboard', icon: Home, end: true, group: 'Dashboard' },
  {
    key: 'attendance', feature: 'ATTENDANCE',
    to: '/parent/attendance',
    label: 'Attendance',
    icon: ClipboardCheck,
    group: 'My child',
  },
  // One screen, two tabs — the work set for the class, and the tasks a family
  // is asked to do. Named for both, because a parent looks for either here.
  {
    key: 'homework', feature: 'HOMEWORK',
    to: '/parent/homework',
    label: 'Homework',
    icon: ClipboardList,
    group: 'My child',
  },
  {
    key: 'timetable', feature: 'TIMETABLE',
    to: '/parent/timetable',
    label: 'Timetable',
    icon: CalendarClock,
    group: 'My child',
  },
  { key: 'results', feature: 'EXAM', to: '/parent/results', label: 'Exams', icon: Award, group: 'My child' },
  {
    key: 'lectures', feature: 'RECORDING',
    to: '/parent/recorded-classes',
    label: 'Recorded Classes',
    icon: Video,
    group: 'My child',
  },
  { key: 'notices', feature: 'CLASS_NOTICE', to: '/parent/notices', label: 'Class Notices', icon: Bell, group: 'School' },
  {
    key: 'calendar', feature: 'CALENDAR',
    to: '/parent/calendar',
    label: 'Academic Calendar',
    icon: CalendarDays,
    group: 'School',
  },
  { key: 'transport', feature: 'TRANSPORT', to: '/parent/transport', label: 'Transport', icon: Bus, group: 'Services' },
  {
    key: 'fees', feature: 'FEES',
    to: '/parent/fees',
    label: 'Fees',
    icon: IndianRupee,
    group: 'Services',
  },
  {
    key: 'profile', feature: 'MY_PROFILE',
    to: '/parent/profile',
    // The page is the child's record, not the parent's own; the catalogue's
    // "My Profile" stays on the student's and the admin's menus.
    label: 'Child Profile',
    keepLabel: true,
    icon: User,
    group: 'Account',
  },
];

/** A school-facing panel, as the platform console names it. */
export type Panel = 'Admin' | 'Teacher' | 'Student' | 'Parent';

/** The school-facing panels, in the order the platform console lists them. */
const PANEL_MENUS: readonly { panel: Panel; nav: readonly NavOption[] }[] = [
  { panel: 'Admin', nav: ADMIN_NAV },
  { panel: 'Teacher', nav: TEACHER_SECTIONS },
  { panel: 'Student', nav: STUDENT_NAV },
  { panel: 'Parent', nav: PARENT_NAV },
];

/**
 * The catalogue's whole-panel switches — whether anybody of that role may sign
 * in at all. They have no sidebar item, so they are named here directly.
 */
const PANEL_SWITCHES: Partial<Record<string, Panel>> = {
  TEACHER_PANEL: 'Teacher',
  STUDENT_PANEL: 'Student',
  PARENT_PANEL: 'Parent',
};

/**
 * Which panels show a catalogue feature on their sidebar — read from the menus
 * above, so the Feature tracker's labels cannot drift from what schools see.
 * Takes a plain string because the catalogue may hold keys no menu uses yet;
 * those name no panel.
 */
export function panelsForFeature(key: string): Panel[] {
  const wholePanel = PANEL_SWITCHES[key];
  if (wholePanel) return [wholePanel];
  return PANEL_MENUS.filter(({ nav }) => nav.some((option) => option.feature === key)).map(
    ({ panel }) => panel,
  );
}

/**
 * Which section an address is asking for. Anything else is the overview.
 *
 * The overview is skipped when matching because its path is a prefix of every
 * other one — `/student` starts `/student/marks` too.
 */
export function navKeyFromPath(options: readonly NavOption[], pathname: string): string {
  const match = options.find(
    (option) => option.key !== 'overview' && pathname.startsWith(option.to),
  );

  return match?.key ?? 'overview';
}

/**
 * Whether a menu entry is open to this account — its permission held and its
 * feature active at the school. Platform staff carry no features and are
 * never gated by one.
 */
export function isNavOptionAvailable(
  option: NavOption,
  permissions: readonly string[] | undefined,
  features: readonly FeatureKey[] | undefined,
): boolean {
  if (option.needs && !permissions?.includes(option.needs)) return false;
  if (option.feature && !features?.includes(option.feature)) return false;
  return true;
}

/**
 * The menu entry whose feature is switched off, if `pathname` is one of its
 * screens — so a layout can show "not enabled" instead of a page that would
 * only be refused by the server. Null when the screen is open.
 */
export function disabledNavOptionAt(
  options: readonly NavOption[],
  pathname: string,
  features: readonly FeatureKey[] | undefined,
): NavOption | null {
  return (
    options.find(
      (option) =>
        option.feature !== undefined &&
        !features?.includes(option.feature) &&
        [option.to, ...(option.matchPaths ?? [])].some(
          // A panel's root (`end`) is a prefix of every page in it, so it is
          // matched exactly — a switched-off dashboard must not close the rest.
          (prefix) => pathname === prefix || (!option.end && pathname.startsWith(`${prefix}/`)),
        ),
    ) ?? null
  );
}

/**
 * What a menu entry is called: the catalogue's name for its feature — one name
 * for each page across every panel and the Roles screen — or its own label
 * when it has no feature, is marked `keepLabel`, or the catalogue has no name.
 */
export function navLabel(
  option: NavOption,
  featureNames: Partial<Record<FeatureKey, string>> | undefined,
): string {
  if (!option.feature || option.keepLabel) return option.label;
  return featureName(option.feature, featureNames);
}
